import { useState, useEffect, useRef } from 'react';
import { 
  Calculator, CheckCircle2, ChevronRight, RefreshCw, Brain, Home, Building2
} from 'lucide-react';

const API = getApiUrl();
import getApiUrl from '../../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Plafonds ANAH 2026 pour MaPrimeAdapt'
const PLAFONDS_ANAH_2026 = {
  idf: {
    1: { tres_modeste: 24031, modeste: 29253 },
    2: { tres_modeste: 35270, modeste: 42933 },
    3: { tres_modeste: 42357, modeste: 51564 },
    4: { tres_modeste: 49455, modeste: 60208 },
    5: { tres_modeste: 56580, modeste: 68877 }
  },
  province: {
    1: { tres_modeste: 17363, modeste: 22259 },
    2: { tres_modeste: 25393, modeste: 32553 },
    3: { tres_modeste: 30540, modeste: 39148 },
    4: { tres_modeste: 35676, modeste: 45735 },
    5: { tres_modeste: 40835, modeste: 52348 }
  }
};

// Produits MPA avec montants moyens
const PRODUITS_MPA = [
  { id: 'salle_de_bain', label: 'Salle de bain / Douche', icon: '🚿', montantMoyen: 6000 },
  { id: 'monte_escalier', label: 'Monte-escalier', icon: '🪜', montantMoyen: 8000 },
  { id: 'volets_roulants', label: 'Volets roulants', icon: '🪟', montantMoyen: 3500 },
  { id: 'rampe_acces', label: 'Rampe d\'accès', icon: '♿', montantMoyen: 2500 },
  { id: 'elargissement_portes', label: 'Élargissement portes', icon: '🚪', montantMoyen: 3000 },
  { id: 'main_courante', label: 'Main courante / Barres', icon: '🤲', montantMoyen: 1500 }
];

// Travaux hors MPA
const TRAVAUX_HORS_MPA = [
  { id: 'cuisine', label: 'Cuisine adaptée', icon: '🍳' },
  { id: 'domotique', label: 'Domotique', icon: '🏠' },
  { id: 'eclairage', label: 'Éclairage adapté', icon: '💡' },
  { id: 'sols', label: 'Revêtements sols', icon: '🏗️' },
  { id: 'rangements', label: 'Rangements', icon: '🗄️' },
  { id: 'chauffage', label: 'Chauffage', icon: '🔥' }
];

/**
 * Widget Simulateur MPA - Composant réutilisable
 * 
 * @param {Object} props
 * @param {Object} props.initialData - Données initiales pour pré-remplir le simulateur
 * @param {Function} props.onEligibiliteChange - Callback quand l'éligibilité change
 * @param {Function} props.onSave - Callback pour sauvegarder la simulation
 * @param {boolean} props.showSaveButton - Afficher le bouton de sauvegarde
 * @param {boolean} props.collapsed - État initial (ouvert/fermé)
 * @param {boolean} props.readOnly - Mode lecture seule
 * @param {string} props.clientId - ID du client pour charger/sauvegarder
 */
const SimulateurMPAWidget = ({ 
  initialData = null,
  onEligibiliteChange = null,
  onSave = null,
  showSaveButton = false,
  collapsed = false,
  readOnly = false,
  clientId = null,
  codePostal = ''
}) => {
  // État du simulateur
  const [simulateurOuvert, setSimulateurOuvert] = useState(!collapsed);
  const [simulationChargee, setSimulationChargee] = useState(false);
  const [loadingSimulation, setLoadingSimulation] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // État de l'éligibilité
  const [eligibilite, setEligibilite] = useState({
    zone: 'idf',
    nbPersonnes: 1,
    rfr: '',
    statutLogement: '',
    typeResidence: '',
    typeLocation: '',
    rechercheBailleur: false,
    nomBailleur: '',
    reponseFatimaBailleur: null,
    typeBien: '',
    ageBeneficiaire: '',
    handicapMDPH: false,
    attestationGIR: false,
    engagementGIR: false,
    dejaBeneficieMPA: '',
    typeMontantConnu: '',
    montantEnveloppeAttribuee: '',
    montantDevisHT: '',
    montantApproximatif: '',
    travauxDejaRealises: [],
    produitsInteresses: [],
    veutTravauxHorsMPA: false,
    travauxHorsMPASelectionnes: [],
    resultat: null,
    motifNonEligible: '',
    tauxPriseEnCharge: 0,
    enveloppeMax: 0,
    montantUtilise: 0,
    montantRestant: 0,
    checked: false
  });
  
  // État pour la section travaux hors MPA
  const [travauxHorsMPAOuvert, setTravauxHorsMPAOuvert] = useState(false);

  // Auto-détection zone IDF/Province depuis code postal
  useEffect(() => {
    if (codePostal && codePostal.length >= 2) {
      const dept = codePostal.substring(0, 2);
      const isIdf = ['75', '77', '78', '91', '92', '93', '94', '95'].includes(dept);
      setEligibilite(prev => {
        const newZone = isIdf ? 'idf' : 'province';
        if (prev.zone !== newZone) return { ...prev, zone: newZone, checked: false };
        return prev;
      });
    }
  }, [codePostal]);


  // Charger la simulation si clientId fourni
  useEffect(() => {
    const loadSavedSimulation = async () => {
      if (!clientId) return;
      
      setLoadingSimulation(true);
      try {
        const response = await authFetch(`${API}/mpa/simulation/${clientId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.found && data.simulation) {
            const sim = data.simulation;
            setEligibilite(prev => ({
              ...prev,
              zone: sim.zone || 'idf',
              nbPersonnes: sim.nb_personnes || 1,
              rfr: sim.rfr?.toString() || '',
              statutLogement: sim.statut_logement || '',
              typeResidence: sim.type_residence || '',
              typeLocation: sim.type_location || '',
              typeBien: sim.type_bien || '',
              ageBeneficiaire: sim.age_beneficiaire?.toString() || '',
              handicapMDPH: sim.handicap_mdph || false,
              attestationGIR: sim.attestation_gir || false,
              dejaBeneficieMPA: sim.deja_beneficie_mpa ? 'oui' : 'non',
              typeMontantConnu: sim.type_montant_connu || '',
              travauxDejaRealises: sim.travaux_deja_realises || [],
              produitsInteresses: sim.produits_interesses || [],
              veutTravauxHorsMPA: sim.veut_travaux_hors_mpa || false,
              travauxHorsMPASelectionnes: sim.travaux_hors_mpa_selectionnes || [],
              resultat: sim.resultat,
              tauxPriseEnCharge: sim.taux_prise_en_charge || 0,
              enveloppeMax: sim.enveloppe_max || 0,
              montantUtilise: sim.montant_utilise || 0,
              montantRestant: sim.montant_restant || 0,
              checked: true
            }));
            setSimulationChargee(true);
            setSimulateurOuvert(false);
          }
        }
      } catch (error) {
        console.error('Erreur chargement simulation:', error);
      } finally {
        setLoadingSimulation(false);
      }
    };
    
    loadSavedSimulation();
  }, [clientId]);

  // Charger les données initiales si fournies (une seule fois)
  const initialDataApplied = useRef(false);
  useEffect(() => {
    if (initialData && !initialDataApplied.current) {
      setEligibilite(prev => ({
        ...prev,
        ...initialData
      }));
    }
    // Toujours activer le flag après le premier rendu pour que
    // onEligibiliteChange se déclenche sur les changements suivants
    initialDataApplied.current = true;
  }, []);

  // Notifier le parent quand l'éligibilité change (sauf au chargement initial)
  useEffect(() => {
    if (onEligibiliteChange && initialDataApplied.current) {
      onEligibiliteChange(eligibilite);
    }
  }, [eligibilite]);

  // Calculer l'éligibilité MPA
  const calculerEligibilite = () => {
    const rfr = parseInt(eligibilite.rfr) || 0;
    const nbPers = Math.min(Math.max(eligibilite.nbPersonnes, 1), 5);
    const plafonds = PLAFONDS_ANAH_2026[eligibilite.zone][nbPers];
    const age = parseInt(eligibilite.ageBeneficiaire) || 0;
    
    let resultat = 'non_eligible';
    let motifNonEligible = '';
    
    // 1. Vérifier le statut logement
    if (!eligibilite.statutLogement) {
      motifNonEligible = 'Statut logement non renseigné';
      setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
      return;
    }
    
    if (eligibilite.statutLogement === 'proprietaire' && eligibilite.typeResidence === 'secondaire') {
      motifNonEligible = 'Résidence secondaire non éligible (doit être résidence principale)';
      setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
      return;
    }
    
    if (eligibilite.statutLogement === 'proprietaire' && !eligibilite.typeResidence) {
      motifNonEligible = 'Veuillez préciser si résidence principale ou secondaire';
      setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
      return;
    }
    
    if (eligibilite.statutLogement === 'locataire' && eligibilite.typeLocation === 'bailleur_social') {
      motifNonEligible = 'Locataire bailleur social non éligible MPA (uniquement parc privé)';
      setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
      return;
    }
    
    if (eligibilite.statutLogement === 'locataire' && !eligibilite.typeLocation) {
      motifNonEligible = 'Veuillez préciser parc privé ou bailleur social';
      setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
      return;
    }
    
    // 2. Vérifier l'âge et les conditions médicales
    if (!age) {
      motifNonEligible = 'Âge du bénéficiaire non renseigné';
      setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
      return;
    }
    
    if (age < 60) {
      if (!eligibilite.handicapMDPH) {
        motifNonEligible = 'Moins de 60 ans : Handicap MDPH ≥ 50% obligatoire';
        setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
        return;
      }
    } else if (age >= 60 && age < 70) {
      if (!eligibilite.handicapMDPH && !eligibilite.attestationGIR && !eligibilite.engagementGIR) {
        motifNonEligible = '60-69 ans : Handicap MDPH ≥ 50% OU Attestation GIR OU Engagement requis';
        setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
        return;
      }
    }
    // 70+ ans : pas de condition médicale
    
    // 3. Vérifier les revenus
    if (!rfr) {
      motifNonEligible = 'RFR non renseigné';
      setEligibilite(prev => ({ ...prev, resultat: 'non_eligible', motifNonEligible, checked: true }));
      return;
    }
    
    if (rfr <= plafonds.tres_modeste) {
      resultat = 'tres_modeste';
    } else if (rfr <= plafonds.modeste) {
      resultat = 'modeste';
    } else {
      motifNonEligible = `Revenus (${rfr.toLocaleString()}€) supérieurs au plafond Modeste (${plafonds.modeste.toLocaleString()}€)`;
    }
    
    // 4. Calculer l'enveloppe et le montant restant
    let tauxPriseEnCharge = 0;
    let enveloppeMax = 0;
    let montantUtilise = 0;
    let montantRestant = 0;
    let isConditionnel = false;
    
    if (resultat === 'tres_modeste') {
      tauxPriseEnCharge = 70;
      enveloppeMax = 15400; // 70% de 22 000€
    } else if (resultat === 'modeste') {
      tauxPriseEnCharge = 50;
      enveloppeMax = 11000; // 50% de 22 000€
    }
    
    // Si a déjà bénéficié, calculer le montant utilisé
    if (eligibilite.dejaBeneficieMPA === 'oui' && enveloppeMax > 0) {
      if (eligibilite.typeMontantConnu === 'enveloppe') {
        montantUtilise = parseInt(eligibilite.montantEnveloppeAttribuee) || 0;
      } else if (eligibilite.typeMontantConnu === 'devis') {
        const devisHT = parseInt(eligibilite.montantDevisHT) || 0;
        montantUtilise = Math.round(devisHT * (tauxPriseEnCharge / 100));
      } else if (eligibilite.typeMontantConnu === 'approximatif') {
        montantUtilise = parseInt(eligibilite.montantApproximatif) || 0;
      } else if (eligibilite.typeMontantConnu === 'ne_sait_pas') {
        isConditionnel = true;
        montantUtilise = 0;
        montantRestant = enveloppeMax;
      }
      
      if (!isConditionnel) {
        montantRestant = Math.max(0, enveloppeMax - montantUtilise);
        
        if (montantRestant === 0) {
          motifNonEligible = `Plafond MPA atteint (${enveloppeMax.toLocaleString()}€ pour ${tauxPriseEnCharge}%) - Proposer autres aides`;
          resultat = 'non_eligible';
        }
      }
    } else {
      montantRestant = enveloppeMax;
    }
    
    // Si conditionnel, modifier le résultat
    if (isConditionnel && resultat !== 'non_eligible') {
      resultat = resultat + '_conditionnel';
    }
    
    setEligibilite(prev => ({ 
      ...prev, 
      resultat, 
      motifNonEligible, 
      tauxPriseEnCharge,
      enveloppeMax,
      montantUtilise,
      montantRestant,
      checked: true 
    }));
  };

  // Recherche bailleur social avec Fatima IA
  const rechercherBailleurAvecFatima = async () => {
    if (!eligibilite.nomBailleur.trim()) return;
    
    setEligibilite(prev => ({ ...prev, reponseFatimaBailleur: 'loading' }));
    
    try {
      const resp = await authFetch(`${API}/fatima/recherche-bailleur`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom_bailleur: eligibilite.nomBailleur })
      });
      
      const data = await resp.json();
      
      if (data.est_bailleur_social) {
        setEligibilite(prev => ({ 
          ...prev, 
          reponseFatimaBailleur: 'oui',
          typeLocation: 'bailleur_social'
        }));
      } else {
        setEligibilite(prev => ({ 
          ...prev, 
          reponseFatimaBailleur: 'non',
          typeLocation: 'prive'
        }));
      }
    } catch (e) {
      console.error("Erreur recherche bailleur:", e);
      setEligibilite(prev => ({ ...prev, reponseFatimaBailleur: null }));
    }
  };

  // Réinitialiser le simulateur
  const resetSimulation = () => {
    setEligibilite({
      zone: 'idf', nbPersonnes: 1, rfr: '', statutLogement: '', typeResidence: '', typeLocation: '',
      rechercheBailleur: false, nomBailleur: '', reponseFatimaBailleur: null, typeBien: '',
      ageBeneficiaire: '', handicapMDPH: false, attestationGIR: false, engagementGIR: false,
      dejaBeneficieMPA: '', typeMontantConnu: '', montantEnveloppeAttribuee: '', montantDevisHT: '',
      montantApproximatif: '', travauxDejaRealises: [], produitsInteresses: [], veutTravauxHorsMPA: false,
      travauxHorsMPASelectionnes: [], resultat: null, motifNonEligible: '', tauxPriseEnCharge: 0,
      enveloppeMax: 0, montantUtilise: 0, montantRestant: 0, checked: false
    });
    setSimulationChargee(false);
    setTravauxHorsMPAOuvert(false);
  };

  // Sauvegarder la simulation
  const handleSave = async () => {
    if (onSave) {
      setSaving(true);
      try {
        await onSave(eligibilite);
        setSimulationChargee(true);
      } catch (error) {
        console.error('Erreur sauvegarde:', error);
      } finally {
        setSaving(false);
      }
    }
  };

  // Obtenir les données de la simulation pour export
  const getSimulationData = () => eligibilite;

  return (
    <div className="bg-gradient-to-r from-violet-900/30 to-purple-900/30 border border-violet-500/30 rounded-xl p-4">
      {/* Header avec toggle */}
      <button
        onClick={() => setSimulateurOuvert(!simulateurOuvert)}
        className="w-full flex items-center justify-between text-left"
      >
        <h3 className="text-sm font-bold text-violet-400 uppercase tracking-wider flex items-center gap-2 flex-wrap">
          <Calculator className="w-4 h-4" />
          Simulateur d'éligibilité MPA
          {loadingSimulation && (
            <span className="bg-slate-500/20 text-slate-400 px-2 py-0.5 rounded text-xs normal-case animate-pulse">
              Chargement...
            </span>
          )}
          {simulationChargee && !loadingSimulation && (
            <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded text-xs normal-case flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Sauvegardée
            </span>
          )}
          {eligibilite.checked && (
            <span className={`px-2 py-0.5 rounded text-xs normal-case ${
              eligibilite.resultat === 'tres_modeste' 
                ? 'bg-emerald-500/20 text-emerald-400'
                : eligibilite.resultat === 'modeste'
                ? 'bg-blue-500/20 text-blue-400'
                : eligibilite.resultat?.includes('_conditionnel')
                ? 'bg-amber-500/20 text-amber-400'
                : 'bg-red-500/20 text-red-400'
            }`}>
              {eligibilite.resultat === 'tres_modeste' && '✓ Très Modeste'}
              {eligibilite.resultat === 'modeste' && '✓ Modeste'}
              {eligibilite.resultat === 'tres_modeste_conditionnel' && '⚠ Très Modeste (sous condition)'}
              {eligibilite.resultat === 'modeste_conditionnel' && '⚠ Modeste (sous condition)'}
              {eligibilite.resultat === 'non_eligible' && '✗ Non éligible'}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Plafonds ANAH 2026</span>
          <ChevronRight className={`w-5 h-5 text-violet-400 transition-transform ${simulateurOuvert ? 'rotate-90' : ''}`} />
        </div>
      </button>
      
      {/* Contenu du simulateur */}
      {simulateurOuvert && (
        <div className="mt-4">
          {/* Bouton réinitialiser */}
          {simulationChargee && (
            <div className="flex justify-end mb-3">
              <button
                onClick={resetSimulation}
                disabled={readOnly}
                className="text-xs text-slate-400 hover:text-white underline disabled:opacity-50"
              >
                Réinitialiser la simulation
              </button>
            </div>
          )}
          
          {/* Ligne 1: Zone, Nb personnes, RFR */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Zone géographique</label>
              <select
                value={eligibilite.zone}
                onChange={e => setEligibilite(prev => ({ ...prev, zone: e.target.value, checked: false }))}
                disabled={readOnly}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-violet-500 focus:outline-none disabled:opacity-50"
              >
                <option value="province">Province</option>
                <option value="idf">Île-de-France</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Nb personnes foyer</label>
              <select
                value={eligibilite.nbPersonnes}
                onChange={e => setEligibilite(prev => ({ ...prev, nbPersonnes: parseInt(e.target.value), checked: false }))}
                disabled={readOnly}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-violet-500 focus:outline-none disabled:opacity-50"
              >
                <option value="1">1 personne</option>
                <option value="2">2 personnes</option>
                <option value="3">3 personnes</option>
                <option value="4">4 personnes</option>
                <option value="5">5+ personnes</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">RFR (Revenu Fiscal Réf.) €</label>
              <input
                type="number"
                value={eligibilite.rfr}
                onChange={e => setEligibilite(prev => ({ ...prev, rfr: e.target.value, checked: false }))}
                disabled={readOnly}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-violet-500 focus:outline-none disabled:opacity-50"
                placeholder="Ex: 18000"
              />
            </div>
          </div>
          
          {/* Plafonds de référence */}
          <div className="flex items-center gap-4 mb-4 p-2 bg-slate-800/50 rounded-lg text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span className="text-slate-400">Très Modeste: ≤ {PLAFONDS_ANAH_2026[eligibilite.zone][eligibilite.nbPersonnes].tres_modeste.toLocaleString()}€</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-slate-400">Modeste: ≤ {PLAFONDS_ANAH_2026[eligibilite.zone][eligibilite.nbPersonnes].modeste.toLocaleString()}€</span>
            </div>
          </div>
          
          {/* Statut logement */}
          <div className="mb-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
            <label className="block text-xs text-slate-400 mb-2 font-medium">🏠 Statut du logement</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="statutLogement"
                      value="proprietaire"
                      checked={eligibilite.statutLogement === 'proprietaire'}
                      onChange={e => setEligibilite(prev => ({ ...prev, statutLogement: e.target.value, typeLocation: '', checked: false }))}
                      disabled={readOnly}
                      className="w-4 h-4 accent-violet-500"
                    />
                    <span className="text-sm text-white">Propriétaire</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="statutLogement"
                      value="locataire"
                      checked={eligibilite.statutLogement === 'locataire'}
                      onChange={e => setEligibilite(prev => ({ ...prev, statutLogement: e.target.value, typeResidence: '', checked: false }))}
                      disabled={readOnly}
                      className="w-4 h-4 accent-violet-500"
                    />
                    <span className="text-sm text-white">Locataire</span>
                  </label>
                </div>
                
                {/* Si propriétaire */}
                {eligibilite.statutLogement === 'proprietaire' && (
                  <div className="flex gap-3 ml-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="typeResidence"
                        value="principale"
                        checked={eligibilite.typeResidence === 'principale'}
                        onChange={e => setEligibilite(prev => ({ ...prev, typeResidence: e.target.value, checked: false }))}
                        disabled={readOnly}
                        className="w-3 h-3 accent-emerald-500"
                      />
                      <span className="text-xs text-slate-300">Résidence principale</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="typeResidence"
                        value="secondaire"
                        checked={eligibilite.typeResidence === 'secondaire'}
                        onChange={e => setEligibilite(prev => ({ ...prev, typeResidence: e.target.value, checked: false }))}
                        disabled={readOnly}
                        className="w-3 h-3 accent-red-500"
                      />
                      <span className="text-xs text-slate-300">Résidence secondaire</span>
                    </label>
                  </div>
                )}
                
                {/* Si locataire */}
                {eligibilite.statutLogement === 'locataire' && (
                  <div className="ml-4 mt-2">
                    <div className="flex gap-3 mb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="typeLocation"
                          value="prive"
                          checked={eligibilite.typeLocation === 'prive'}
                          onChange={e => setEligibilite(prev => ({ ...prev, typeLocation: e.target.value, checked: false }))}
                          disabled={readOnly}
                          className="w-3 h-3 accent-emerald-500"
                        />
                        <span className="text-xs text-slate-300">Parc privé</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="typeLocation"
                          value="bailleur_social"
                          checked={eligibilite.typeLocation === 'bailleur_social'}
                          onChange={e => setEligibilite(prev => ({ ...prev, typeLocation: e.target.value, checked: false }))}
                          disabled={readOnly}
                          className="w-3 h-3 accent-red-500"
                        />
                        <span className="text-xs text-slate-300">Bailleur social</span>
                      </label>
                    </div>
                    
                    {/* Recherche bailleur avec Fatima */}
                    <label className="flex items-center gap-2 cursor-pointer mt-2">
                      <input
                        type="checkbox"
                        checked={eligibilite.rechercheBailleur}
                        onChange={e => setEligibilite(prev => ({ ...prev, rechercheBailleur: e.target.checked }))}
                        disabled={readOnly}
                        className="w-3 h-3 accent-purple-500"
                      />
                      <span className="text-xs text-purple-400">🔍 Je ne sais pas, rechercher avec Fatima IA</span>
                    </label>
                    
                    {eligibilite.rechercheBailleur && (
                      <div className="mt-2 p-2 bg-purple-900/20 rounded-lg border border-purple-500/30">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={eligibilite.nomBailleur}
                            onChange={e => setEligibilite(prev => ({ ...prev, nomBailleur: e.target.value }))}
                            disabled={readOnly}
                            className="flex-1 px-3 py-1.5 bg-slate-900 border border-slate-600 rounded text-white text-xs placeholder:text-slate-500 focus:border-purple-500 focus:outline-none"
                            placeholder="Nom du bailleur (ex: Paris Habitat, OPAC...)"
                          />
                          <button
                            onClick={rechercherBailleurAvecFatima}
                            disabled={!eligibilite.nomBailleur.trim() || eligibilite.reponseFatimaBailleur === 'loading' || readOnly}
                            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 text-white rounded text-xs flex items-center gap-1"
                          >
                            {eligibilite.reponseFatimaBailleur === 'loading' ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Brain className="w-3 h-3" />
                            )}
                            Demander à Fatima
                          </button>
                        </div>
                        {eligibilite.reponseFatimaBailleur === 'oui' && (
                          <p className="mt-2 text-xs text-red-400">⚠️ "{eligibilite.nomBailleur}" est un bailleur social → Non éligible MPA</p>
                        )}
                        {eligibilite.reponseFatimaBailleur === 'non' && (
                          <p className="mt-2 text-xs text-emerald-400">✓ "{eligibilite.nomBailleur}" n'est pas un bailleur social → Parc privé OK</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Colonne droite: Âge */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">👤 Âge du bénéficiaire</label>
                <input
                  type="number"
                  value={eligibilite.ageBeneficiaire}
                  onChange={e => setEligibilite(prev => ({ ...prev, ageBeneficiaire: e.target.value, checked: false }))}
                  disabled={readOnly}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-violet-500 focus:outline-none disabled:opacity-50"
                  placeholder="Ex: 72"
                  min="0"
                  max="120"
                />
                {eligibilite.ageBeneficiaire && parseInt(eligibilite.ageBeneficiaire) >= 70 && (
                  <p className="text-xs text-emerald-400 mt-1">✓ 70+ ans : Pas de condition médicale</p>
                )}
                {eligibilite.ageBeneficiaire && parseInt(eligibilite.ageBeneficiaire) >= 60 && parseInt(eligibilite.ageBeneficiaire) < 70 && (
                  <p className="text-xs text-amber-400 mt-1">⚠️ 60-69 ans : Condition médicale requise</p>
                )}
                {eligibilite.ageBeneficiaire && parseInt(eligibilite.ageBeneficiaire) < 60 && (
                  <p className="text-xs text-orange-400 mt-1">⚠️ &lt;60 ans : Handicap MDPH ≥50% requis</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Conditions médicales (si < 70 ans) */}
          {eligibilite.ageBeneficiaire && parseInt(eligibilite.ageBeneficiaire) < 70 && (
            <div className="mb-4 p-3 bg-amber-900/20 rounded-lg border border-amber-500/30">
              <label className="block text-xs text-amber-400 mb-2 font-medium">📋 Conditions médicales (obligatoire si &lt; 70 ans)</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={eligibilite.handicapMDPH}
                    onChange={e => setEligibilite(prev => ({ ...prev, handicapMDPH: e.target.checked, checked: false }))}
                    disabled={readOnly}
                    className="w-4 h-4 accent-amber-500"
                  />
                  <span className="text-sm text-white">Handicap MDPH ≥ 50%</span>
                  <span className="text-xs text-slate-400">(Notification MDPH, CMI, ou AAH)</span>
                </label>
                
                {parseInt(eligibilite.ageBeneficiaire) >= 60 && (
                  <>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={eligibilite.attestationGIR}
                        onChange={e => setEligibilite(prev => ({ ...prev, attestationGIR: e.target.checked, checked: false }))}
                        disabled={readOnly}
                        className="w-4 h-4 accent-amber-500"
                      />
                      <span className="text-sm text-white">Attestation GIR (1 à 6)</span>
                      <span className="text-xs text-slate-400">(déjà en possession)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={eligibilite.engagementGIR}
                        onChange={e => setEligibilite(prev => ({ ...prev, engagementGIR: e.target.checked, checked: false }))}
                        disabled={readOnly}
                        className="w-4 h-4 accent-amber-500"
                      />
                      <span className="text-sm text-white">S'engage à demander l'attestation GIR</span>
                      <span className="text-xs text-slate-400">(à son médecin traitant)</span>
                    </label>
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Type de bien */}
          <div className="mb-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
            <label className="block text-xs text-slate-400 mb-2 font-medium">🏗️ Type de bien</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="typeBien"
                  value="maison"
                  checked={eligibilite.typeBien === 'maison'}
                  onChange={e => setEligibilite(prev => ({ ...prev, typeBien: e.target.value, checked: false }))}
                  disabled={readOnly}
                  className="w-4 h-4 accent-emerald-500"
                />
                <Home className="w-4 h-4 text-white" />
                <span className="text-sm text-white">Maison</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="typeBien"
                  value="appartement"
                  checked={eligibilite.typeBien === 'appartement'}
                  onChange={e => setEligibilite(prev => ({ ...prev, typeBien: e.target.value, checked: false }))}
                  disabled={readOnly}
                  className="w-4 h-4 accent-blue-500"
                />
                <Building2 className="w-4 h-4 text-white" />
                <span className="text-sm text-white">Appartement</span>
              </label>
            </div>
            {eligibilite.typeBien === 'maison' && (
              <p className="text-xs text-emerald-400 mt-2">✓ Maison : Éligible aux aides ANAH supplémentaires (PAC, Isolation, Réno Ampleur)</p>
            )}
          </div>
          
          {/* Déjà bénéficié MPA */}
          <div className="mb-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
            <label className="block text-xs text-slate-400 mb-2 font-medium">❓ A déjà bénéficié de MaPrimeAdapt' ?</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="dejaBeneficieMPA"
                  value="non"
                  checked={eligibilite.dejaBeneficieMPA === 'non'}
                  onChange={e => setEligibilite(prev => ({ 
                    ...prev, 
                    dejaBeneficieMPA: e.target.value, 
                    typeMontantConnu: '',
                    montantEnveloppeAttribuee: '',
                    montantDevisHT: '',
                    montantApproximatif: '',
                    travauxDejaRealises: [], 
                    checked: false 
                  }))}
                  disabled={readOnly}
                  className="w-4 h-4 accent-emerald-500"
                />
                <span className="text-sm text-white">Non</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="dejaBeneficieMPA"
                  value="oui"
                  checked={eligibilite.dejaBeneficieMPA === 'oui'}
                  onChange={e => setEligibilite(prev => ({ ...prev, dejaBeneficieMPA: e.target.value, checked: false }))}
                  disabled={readOnly}
                  className="w-4 h-4 accent-amber-500"
                />
                <span className="text-sm text-white">Oui</span>
              </label>
            </div>
            
            {/* Détails si oui */}
            {eligibilite.dejaBeneficieMPA === 'oui' && (
              <div className="mt-3 p-3 bg-amber-900/20 rounded-lg border border-amber-500/30">
                {/* Type de montant connu */}
                <div className="mb-4">
                  <label className="block text-xs text-amber-400 mb-2 font-medium">💰 Comment connaître le montant déjà utilisé ?</label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                      eligibilite.typeMontantConnu === 'enveloppe' ? 'bg-amber-500/20 border-amber-500/50' : 'bg-slate-800/50 border-slate-700'
                    }`}>
                      <input
                        type="radio"
                        name="typeMontantConnu"
                        value="enveloppe"
                        checked={eligibilite.typeMontantConnu === 'enveloppe'}
                        onChange={e => setEligibilite(prev => ({ ...prev, typeMontantConnu: e.target.value, checked: false }))}
                        disabled={readOnly}
                        className="w-3 h-3 accent-amber-500"
                      />
                      <span className="text-xs text-white">Montant enveloppe ANAH</span>
                    </label>
                    <label className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                      eligibilite.typeMontantConnu === 'devis' ? 'bg-amber-500/20 border-amber-500/50' : 'bg-slate-800/50 border-slate-700'
                    }`}>
                      <input
                        type="radio"
                        name="typeMontantConnu"
                        value="devis"
                        checked={eligibilite.typeMontantConnu === 'devis'}
                        onChange={e => setEligibilite(prev => ({ ...prev, typeMontantConnu: e.target.value, checked: false }))}
                        disabled={readOnly}
                        className="w-3 h-3 accent-amber-500"
                      />
                      <span className="text-xs text-white">Montant devis HT</span>
                    </label>
                    <label className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                      eligibilite.typeMontantConnu === 'approximatif' ? 'bg-amber-500/20 border-amber-500/50' : 'bg-slate-800/50 border-slate-700'
                    }`}>
                      <input
                        type="radio"
                        name="typeMontantConnu"
                        value="approximatif"
                        checked={eligibilite.typeMontantConnu === 'approximatif'}
                        onChange={e => setEligibilite(prev => ({ ...prev, typeMontantConnu: e.target.value, checked: false }))}
                        disabled={readOnly}
                        className="w-3 h-3 accent-amber-500"
                      />
                      <span className="text-xs text-white">Montant approximatif</span>
                    </label>
                    <label className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                      eligibilite.typeMontantConnu === 'ne_sait_pas' ? 'bg-amber-500/20 border-amber-500/50' : 'bg-slate-800/50 border-slate-700'
                    }`}>
                      <input
                        type="radio"
                        name="typeMontantConnu"
                        value="ne_sait_pas"
                        checked={eligibilite.typeMontantConnu === 'ne_sait_pas'}
                        onChange={e => setEligibilite(prev => ({ ...prev, typeMontantConnu: e.target.value, checked: false }))}
                        disabled={readOnly}
                        className="w-3 h-3 accent-amber-500"
                      />
                      <span className="text-xs text-white">Ne sait pas</span>
                    </label>
                  </div>
                </div>
                
                {/* Champs selon le type */}
                {eligibilite.typeMontantConnu === 'enveloppe' && (
                  <div className="mb-4">
                    <label className="block text-xs text-amber-400 mb-1">Montant enveloppe ANAH (€)</label>
                    <input
                      type="number"
                      value={eligibilite.montantEnveloppeAttribuee}
                      onChange={e => setEligibilite(prev => ({ ...prev, montantEnveloppeAttribuee: e.target.value, checked: false }))}
                      disabled={readOnly}
                      className="w-full px-3 py-2 bg-slate-900 border border-amber-500/50 rounded-lg text-white text-sm"
                      placeholder="Ex: 8000"
                    />
                  </div>
                )}
                
                {eligibilite.typeMontantConnu === 'devis' && (
                  <div className="mb-4">
                    <label className="block text-xs text-amber-400 mb-1">Montant devis HT (€)</label>
                    <input
                      type="number"
                      value={eligibilite.montantDevisHT}
                      onChange={e => setEligibilite(prev => ({ ...prev, montantDevisHT: e.target.value, checked: false }))}
                      disabled={readOnly}
                      className="w-full px-3 py-2 bg-slate-900 border border-amber-500/50 rounded-lg text-white text-sm"
                      placeholder="Ex: 12000"
                    />
                    <p className="text-xs text-slate-500 mt-1">Enveloppe = Devis × taux</p>
                  </div>
                )}
                
                {eligibilite.typeMontantConnu === 'approximatif' && (
                  <div className="mb-4">
                    <label className="block text-xs text-amber-400 mb-1">Montant approximatif (€)</label>
                    <input
                      type="number"
                      value={eligibilite.montantApproximatif}
                      onChange={e => setEligibilite(prev => ({ ...prev, montantApproximatif: e.target.value, checked: false }))}
                      disabled={readOnly}
                      className="w-full px-3 py-2 bg-slate-900 border border-amber-500/50 rounded-lg text-white text-sm"
                      placeholder="Ex: 6000"
                    />
                  </div>
                )}
                
                {eligibilite.typeMontantConnu === 'ne_sait_pas' && (
                  <div className="mb-4 p-2 bg-slate-800/50 rounded-lg border border-slate-600">
                    <p className="text-xs text-slate-400">
                      ⚠️ Sans le montant, l'éligibilité sera conditionnelle.
                    </p>
                  </div>
                )}
                
                {/* Travaux déjà réalisés */}
                <div className="mb-3">
                  <label className="block text-xs text-amber-400 mb-2 font-medium">🔧 Travaux déjà réalisés avec MPA</label>
                  <div className="grid grid-cols-2 gap-2">
                    {PRODUITS_MPA.map(produit => (
                      <label 
                        key={produit.id} 
                        className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                          eligibilite.travauxDejaRealises.includes(produit.id)
                            ? 'bg-amber-500/20 border-amber-500/50'
                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={eligibilite.travauxDejaRealises.includes(produit.id)}
                          onChange={e => {
                            const newTravaux = e.target.checked
                              ? [...eligibilite.travauxDejaRealises, produit.id]
                              : eligibilite.travauxDejaRealises.filter(id => id !== produit.id);
                            setEligibilite(prev => ({ ...prev, travauxDejaRealises: newTravaux, checked: false }));
                          }}
                          disabled={readOnly}
                          className="w-3 h-3 accent-amber-500"
                        />
                        <span className="text-sm">{produit.icon}</span>
                        <span className="text-xs text-white">{produit.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Produits qui intéressent */}
          <div className="mb-4 p-3 bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-lg border border-blue-500/30">
            <label className="block text-xs text-blue-400 mb-2 font-medium">
              🎯 Produits qui intéressent le client
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PRODUITS_MPA.filter(p => !eligibilite.travauxDejaRealises.includes(p.id)).map(produit => (
                <label 
                  key={produit.id} 
                  className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                    eligibilite.produitsInteresses.includes(produit.id)
                      ? 'bg-blue-500/30 border-blue-500/50'
                      : 'bg-slate-800/50 border-slate-700 hover:border-blue-500/30'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={eligibilite.produitsInteresses.includes(produit.id)}
                    onChange={e => {
                      const newProduits = e.target.checked
                        ? [...eligibilite.produitsInteresses, produit.id]
                        : eligibilite.produitsInteresses.filter(id => id !== produit.id);
                      setEligibilite(prev => ({ ...prev, produitsInteresses: newProduits }));
                    }}
                    disabled={readOnly}
                    className="w-3 h-3 accent-blue-500"
                  />
                  <span className="text-sm">{produit.icon}</span>
                  <div className="flex-1">
                    <span className="text-xs text-white">{produit.label}</span>
                    <span className="text-xs text-slate-500 ml-1">(~{produit.montantMoyen.toLocaleString()}€)</span>
                  </div>
                </label>
              ))}
            </div>
            {eligibilite.produitsInteresses.length > 0 && (
              <div className="mt-3 p-2 bg-blue-900/30 rounded-lg border border-blue-500/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-blue-300">
                    {eligibilite.produitsInteresses.length} produit(s)
                  </span>
                  <span className="text-xs text-blue-400 font-medium">
                    Estimation: ~{PRODUITS_MPA
                      .filter(p => eligibilite.produitsInteresses.includes(p.id))
                      .reduce((sum, p) => sum + p.montantMoyen, 0)
                      .toLocaleString()}€
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Boutons */}
          <div className="flex items-center gap-4">
            <button
              onClick={calculerEligibilite}
              disabled={readOnly}
              className="px-6 py-2 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              Vérifier l'éligibilité
            </button>
            
            {showSaveButton && eligibilite.checked && (
              <button
                onClick={handleSave}
                disabled={saving || readOnly}
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Sauvegarder
              </button>
            )}
            
            {/* Résultat */}
            {eligibilite.checked && (
              <div className={`flex-1 p-3 rounded-lg border ${
                eligibilite.resultat === 'tres_modeste' 
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : eligibilite.resultat === 'modeste'
                  ? 'bg-blue-500/10 border-blue-500/30'
                  : eligibilite.resultat?.includes('_conditionnel')
                  ? 'bg-amber-500/10 border-amber-500/30'
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                {eligibilite.resultat === 'tres_modeste' && (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-emerald-400 font-bold">✓ Éligible MPA - Très Modeste</span>
                      <p className="text-xs text-slate-400 mt-1">
                        Taux: <span className="text-emerald-400 font-medium">70%</span> • 
                        Enveloppe: <span className="text-emerald-400 font-medium">{eligibilite.enveloppeMax.toLocaleString()}€</span>
                        {eligibilite.montantUtilise > 0 && (
                          <> • Utilisé: <span className="text-amber-400 font-medium">{eligibilite.montantUtilise.toLocaleString()}€</span></>
                        )}
                        {eligibilite.montantRestant > 0 && eligibilite.montantRestant < eligibilite.enveloppeMax && (
                          <> • <span className="text-emerald-300 font-bold">Reste: {eligibilite.montantRestant.toLocaleString()}€</span></>
                        )}
                      </p>
                    </div>
                    <span className="text-2xl">🎉</span>
                  </div>
                )}
                {eligibilite.resultat === 'modeste' && (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-blue-400 font-bold">✓ Éligible MPA - Modeste</span>
                      <p className="text-xs text-slate-400 mt-1">
                        Taux: <span className="text-blue-400 font-medium">50%</span> • 
                        Enveloppe: <span className="text-blue-400 font-medium">{eligibilite.enveloppeMax.toLocaleString()}€</span>
                        {eligibilite.montantUtilise > 0 && (
                          <> • Utilisé: <span className="text-amber-400 font-medium">{eligibilite.montantUtilise.toLocaleString()}€</span></>
                        )}
                        {eligibilite.montantRestant > 0 && eligibilite.montantRestant < eligibilite.enveloppeMax && (
                          <> • <span className="text-blue-300 font-bold">Reste: {eligibilite.montantRestant.toLocaleString()}€</span></>
                        )}
                      </p>
                    </div>
                    <span className="text-2xl">👍</span>
                  </div>
                )}
                {eligibilite.resultat?.includes('_conditionnel') && (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-amber-400 font-bold">⚠️ Éligible - SOUS CONDITION</span>
                      <p className="text-xs text-slate-400 mt-1">
                        Taux: <span className="text-amber-400 font-medium">{eligibilite.tauxPriseEnCharge}%</span> • 
                        Enveloppe max: <span className="text-amber-400 font-medium">{eligibilite.enveloppeMax.toLocaleString()}€</span>
                      </p>
                      <p className="text-xs text-amber-300 mt-1 font-medium">
                        À valider après confirmation du montant déjà perçu
                      </p>
                    </div>
                    <span className="text-2xl">⏳</span>
                  </div>
                )}
                {eligibilite.resultat === 'non_eligible' && (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-red-400 font-bold">✗ Non éligible MPA</span>
                      <p className="text-xs text-slate-400 mt-1">{eligibilite.motifNonEligible || 'Proposer travaux hors prime'}</p>
                    </div>
                    <span className="text-2xl">💡</span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Travaux hors MPA */}
          {eligibilite.checked && (
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-500/30">
              <button
                onClick={() => setTravauxHorsMPAOuvert(!travauxHorsMPAOuvert)}
                className="w-full flex items-center justify-between text-left"
              >
                <h4 className="text-sm font-bold text-purple-400 flex items-center gap-2">
                  🔧 Travaux hors subvention MPA
                  {eligibilite.travauxHorsMPASelectionnes.length > 0 && (
                    <span className="bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded text-xs">
                      {eligibilite.travauxHorsMPASelectionnes.length} sélectionné(s)
                    </span>
                  )}
                </h4>
                <ChevronRight className={`w-5 h-5 text-purple-400 transition-transform ${travauxHorsMPAOuvert ? 'rotate-90' : ''}`} />
              </button>
              
              {travauxHorsMPAOuvert && (
                <div className="mt-3">
                  <p className="text-xs text-slate-400 mb-3">
                    {eligibilite.resultat === 'non_eligible' 
                      ? 'Le client n\'est pas éligible MPA mais peut être intéressé par des travaux sans subvention :'
                      : 'En plus des travaux MPA, le client peut aussi être intéressé par :'
                    }
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {TRAVAUX_HORS_MPA.map(travail => (
                      <label 
                        key={travail.id} 
                        className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${
                          eligibilite.travauxHorsMPASelectionnes.includes(travail.id)
                            ? 'bg-purple-500/30 border-purple-500/50'
                            : 'bg-slate-800/50 border-slate-700 hover:border-purple-500/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={eligibilite.travauxHorsMPASelectionnes.includes(travail.id)}
                          onChange={e => {
                            const newTravaux = e.target.checked
                              ? [...eligibilite.travauxHorsMPASelectionnes, travail.id]
                              : eligibilite.travauxHorsMPASelectionnes.filter(id => id !== travail.id);
                            setEligibilite(prev => ({ ...prev, travauxHorsMPASelectionnes: newTravaux }));
                          }}
                          disabled={readOnly}
                          className="w-3 h-3 accent-purple-500"
                        />
                        <span className="text-sm">{travail.icon}</span>
                        <span className="text-xs text-white">{travail.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimulateurMPAWidget;
